import { useState, useMemo, useEffect } from 'react';
import { Loader2, Check, Plus, Trash2, ArrowLeft } from 'lucide-react';
import { useActiveReceiptCategories, useReceiptConcepts, useReceiptMutations } from '../hooks/useReceipts';
import { useActiveBankAccounts } from '@/features/settings/hooks/useBankAccounts';
import { amountToWords } from '../utils/numberToWords';
import { formatReceiptNumber } from '../types';
import { useToast } from '@/hooks/use-toast';
import { PAYMENT_METHOD_LABELS, formatMoney } from '../utils/receiptFormat';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { isValidQuantity, isLineTouched, isLineValid } from '../utils/lineValidation';
import type {
  ReceiptCurrency, ReceiptPaymentMethod, ReceiptLineInput, ReceiptPaymentInput,
  IssueReceiptInput, AffiliationCustom,
} from '../types';

interface Props {
  /** Locks the category (e.g. issuing a Diezmos receipt from a tithe record). */
  lockedCategoryId?: string;
  /** Preselects a category by code but leaves it editable (e.g. 'ING' for event payments). */
  defaultCategoryCode?: string;
  initialPayerName?: string;
  initialPayerCi?: string;
  initialCurrency?: ReceiptCurrency;
  initialExchangeRate?: string;
  /** Links the receipt to a minister so it shows in their history. */
  ministerId?: string;
  /** Prefills the Afiliación block (only shown for that category). */
  initialAffiliation?: Partial<AffiliationCustom>;
  sourceType?: string;
  sourceId?: string;
  initialLines?: ReceiptLineInput[];
  initialPayments?: ReceiptPaymentInput[];
  onClose: () => void;
  onIssued: (result: { id: string; number: number; year: number }) => void;
}

const EMPTY_LINE: ReceiptLineInput = { concept_id: '', description: '', quantity: '1', unit_price: '' };
const EMPTY_PAYMENT: ReceiptPaymentInput = {
  method: 'cash', amount: '', bank_account_id: '', check_number: '', voucher_photo_url: '',
};

export function IssueReceiptForm({
  lockedCategoryId, defaultCategoryCode, initialPayerName, initialPayerCi, initialCurrency, initialExchangeRate,
  ministerId, initialAffiliation, sourceType, sourceId, initialLines, initialPayments,
  onClose, onIssued,
}: Props) {
  const { data: categories = [] } = useActiveReceiptCategories();
  const { data: concepts = [] } = useReceiptConcepts();
  const { data: bankAccounts = [] } = useActiveBankAccounts();
  const { issue } = useReceiptMutations();
  const { toast } = useToast();

  const [categoryId, setCategoryId] = useState(lockedCategoryId ?? '');

  // Categories load asynchronously: apply the default once, only if the user hasn't picked one
  useEffect(() => {
    if (!defaultCategoryCode || lockedCategoryId) return;
    const match = categories.find((c) => c.code === defaultCategoryCode);
    if (match) setCategoryId((current) => current || match.id);
  }, [categories, defaultCategoryCode, lockedCategoryId]);
  const [payerName, setPayerName] = useState(initialPayerName ?? '');
  const [payerCi, setPayerCi] = useState(initialPayerCi ?? '');
  const [currency, setCurrency] = useState<ReceiptCurrency>(initialCurrency ?? 'BOB');
  const [exchangeRate, setExchangeRate] = useState(initialExchangeRate ?? '');
  const [affiliation, setAffiliation] = useState<AffiliationCustom>({
    adb_code: '', affiliation_date: '', district: '', region: '', ...initialAffiliation,
  });
  const [discount, setDiscount] = useState('');
  const [lines, setLines] = useState<ReceiptLineInput[]>(
    initialLines && initialLines.length > 0 ? initialLines : [{ ...EMPTY_LINE }],
  );
  const [payments, setPayments] = useState<ReceiptPaymentInput[]>(
    initialPayments && initialPayments.length > 0 ? initialPayments : [{ ...EMPTY_PAYMENT }],
  );
  const [error, setError] = useState('');
  const [step, setStep] = useState<'edit' | 'review'>('edit');
  // A single payment line mirrors the total until the user edits payments themselves.
  // Lines handed in by the caller (e.g. from a tithe) are already deliberate.
  const [paymentsTouched, setPaymentsTouched] = useState(!!initialPayments?.length);

  const subtotal = useMemo(
    () => lines.reduce((sum, l) => {
      const q = parseFloat(l.quantity) || 0;
      const p = parseFloat(l.unit_price) || 0;
      return sum + q * p;
    }, 0),
    [lines],
  );
  const discountNum = parseFloat(discount) || 0;
  const total = Math.max(0, subtotal - discountNum);
  const paymentsSum = useMemo(
    () => payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
    [payments],
  );
  const totalInWords = amountToWords(total, currency);

  const isAffiliation = categories.find((c) => c.id === categoryId)?.code === 'AFI';

  const missing = useMemo(() => {
    const items: string[] = [];
    if (!categoryId) items.push('Categoría');
    if (isAffiliation) {
      if (!affiliation.affiliation_date) items.push('Fecha de afiliación');
      if (!affiliation.adb_code.trim()) items.push('Código ADB');
    }
    if (!payerName.trim()) items.push('Nombre del pagador');
    if (currency === 'USD' && !exchangeRate) items.push('Tipo de cambio');
    if (discountNum > subtotal) items.push('El descuento no puede superar el subtotal');

    if (!lines.some(isLineTouched)) {
      items.push('Al menos un ítem con descripción, cantidad y precio');
    } else {
      lines.forEach((l, i) => {
        if (!isLineTouched(l)) return;
        if (!l.description.trim()) items.push(`Descripción del ítem ${i + 1}`);
        if (!isValidQuantity(l.quantity)) items.push(`Cantidad del ítem ${i + 1}: número entero mayor a 0`);
        if (l.unit_price.trim() === '' || parseFloat(l.unit_price) < 0) items.push(`Precio unitario del ítem ${i + 1}`);
      });
    }

    if (total > 0) {
      const validPayments = payments.filter((p) => parseFloat(p.amount) > 0);
      if (validPayments.length === 0) items.push('Al menos una forma de pago');
      else if (Math.abs(paymentsSum - total) > 0.01) items.push(`El pago (${paymentsSum.toFixed(2)}) debe sumar el total (${total.toFixed(2)})`);
      payments.forEach((p, i) => {
        if (p.method === 'check' && parseFloat(p.amount) > 0 && !p.check_number.trim()) {
          items.push(`N° de cheque (pago ${i + 1})`);
        }
      });
    }

    return items;
  }, [categoryId, isAffiliation, affiliation, payerName, currency, exchangeRate, discountNum, subtotal, lines, total, payments, paymentsSum]);

  const isValid = missing.length === 0;

  useEffect(() => {
    if (paymentsTouched || payments.length !== 1 || total <= 0) return;
    setPayments((prev) => [{ ...prev[0], amount: total.toFixed(2) }]);
  }, [total, paymentsTouched, payments.length]);

  const updateLine = (i: number, patch: Partial<ReceiptLineInput>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const updatePayment = (i: number, patch: Partial<ReceiptPaymentInput>) => {
    if ('amount' in patch) setPaymentsTouched(true);
    setPayments((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  };
  const addPayment = () => {
    setPaymentsTouched(true);
    // A split almost always mixes methods, so the new line starts as the other one.
    setPayments((prev) => {
      const last = prev[prev.length - 1]?.method;
      return [...prev, { ...EMPTY_PAYMENT, method: last === 'cash' ? 'bank_transfer' : 'cash' }];
    });
  };
  const removePayment = (i: number) => {
    setPaymentsTouched(true);
    setPayments((prev) => prev.filter((_, idx) => idx !== i));
  };

  const categoryLabel = categories.find((c) => c.id === categoryId);
  const bankLabel = (id: string) => bankAccounts.find((b) => b.id === id)?.account_name ?? '';
  const reviewLines = lines.filter(isLineValid);
  const reviewPayments = payments.filter((p) => parseFloat(p.amount) > 0);

  const handleSubmit = async () => {
    if (!isValid) return;
    setError('');
    const input: IssueReceiptInput = {
      category_id: categoryId,
      payer_name: payerName,
      payer_ci: payerCi,
      currency,
      exchange_rate: exchangeRate,
      discount,
      source_type: sourceType ?? null,
      source_id: sourceId ?? null,
      minister_id: ministerId ?? null,
      custom: isAffiliation
        ? { ...affiliation, adb_code: affiliation.adb_code.trim(), district: affiliation.district.trim(), region: affiliation.region.trim() }
        : undefined,
      lines: lines.filter(isLineValid),
      payments: payments.filter((p) => parseFloat(p.amount) > 0),
    };
    try {
      const result = await issue.mutateAsync({ input, totalInWords });
      toast({ title: `Recibo ${formatReceiptNumber(result.number)} emitido` });
      onIssued(result);
    } catch (e) {
      console.error(e);
      setError('Error al emitir el recibo. Intenta de nuevo.');
    }
  };

  const cur = currency === 'USD' ? 'USD' : 'Bs.';
  const INPUT = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  const CELL  = 'rounded-md border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring';

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !issue.isPending) onClose(); }}>
      <DialogContent
        onInteractOutside={(e) => { if (issue.isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (issue.isPending) e.preventDefault(); }}
        className="max-h-[90vh] max-w-3xl overflow-y-auto max-sm:h-dvh max-sm:max-h-dvh max-sm:max-w-none max-sm:rounded-none"
      >
        <DialogHeader>
          <DialogTitle>{step === 'review' ? 'Confirmar recibo' : 'Emitir recibo'}</DialogTitle>
          <DialogDescription className={step === 'review' ? undefined : 'sr-only'}>
            {step === 'review'
              ? 'Revise los datos: el número del recibo no se puede reutilizar una vez emitido.'
              : 'Complete los datos del recibo oficial.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'review' ? (
          <div className="space-y-4 text-sm">
            <div className="grid gap-3 rounded-md bg-muted/30 p-3 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Categoría</p>
                <p className="font-medium">{categoryLabel ? categoryLabel.name : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Recibí de</p>
                <p className="font-medium">{payerName}{payerCi && ` · C.I. ${payerCi}`}</p>
              </div>
            </div>

            {isAffiliation && (
              <div className="grid gap-1 rounded-md border border-border p-3 text-sm sm:grid-cols-2">
                <p><span className="text-muted-foreground">Fecha de afiliación: </span>{affiliation.affiliation_date || '—'}</p>
                <p><span className="text-muted-foreground">Código ADB: </span>{affiliation.adb_code || '—'}</p>
                <p><span className="text-muted-foreground">Distrito: </span>{affiliation.district || '—'}</p>
                <p><span className="text-muted-foreground">Región: </span>{affiliation.region || '—'}</p>
              </div>
            )}

            <div className="space-y-1">
              {reviewLines.map((l, i) => (
                <div key={i} className="flex justify-between gap-3 border-b border-border/50 py-1">
                  <span>{l.description} <span className="text-muted-foreground">× {l.quantity}</span></span>
                  <span className="shrink-0">{formatMoney((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0))}</span>
                </div>
              ))}
              {discountNum > 0 && (
                <div className="flex justify-between py-1 text-muted-foreground">
                  <span>Descuento</span><span>- {formatMoney(discountNum)}</span>
                </div>
              )}
              <div className="flex justify-between pt-1 text-base font-bold">
                <span>Total</span><span>{formatMoney(total)} {cur}</span>
              </div>
              {currency === 'USD' && <p className="text-right text-xs text-muted-foreground">T/C: {exchangeRate}</p>}
              <p className="text-xs italic text-muted-foreground">Son: {totalInWords}</p>
            </div>

            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Forma de pago</p>
              {reviewPayments.map((p, i) => (
                <div key={i} className="flex justify-between gap-3">
                  <span>
                    {PAYMENT_METHOD_LABELS[p.method]}
                    {p.check_number && ` N° ${p.check_number}`}
                    {p.bank_account_id && bankLabel(p.bank_account_id) && ` · ${bankLabel(p.bank_account_id)}`}
                  </span>
                  <span>{formatMoney(parseFloat(p.amount))}</span>
                </div>
              ))}
              {reviewPayments.length === 0 && <p className="text-muted-foreground">—</p>}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setStep('edit')} disabled={issue.isPending}>
                <ArrowLeft className="mr-1.5 size-4" /> Volver
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={issue.isPending || !isValid}>
                {issue.isPending ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Check className="mr-1.5 size-4" />}
                Confirmar y emitir
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Category */}
            <div>
              <label className="mb-1 block text-sm font-medium">Categoría <span className="text-destructive">*</span></label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={!!lockedCategoryId || issue.isPending}
                className={`${INPUT} disabled:opacity-60`}
              >
                <option value="">— Seleccionar —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {isAffiliation && (
              <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Datos de afiliación</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium">Fecha de afiliación <span className="text-destructive">*</span></label>
                    <input
                      type="date" value={affiliation.affiliation_date}
                      onChange={(e) => setAffiliation((a) => ({ ...a, affiliation_date: e.target.value }))}
                      disabled={issue.isPending} className={INPUT}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium">Código ADB <span className="text-destructive">*</span></label>
                    <input
                      value={affiliation.adb_code}
                      onChange={(e) => setAffiliation((a) => ({ ...a, adb_code: e.target.value }))}
                      disabled={issue.isPending} className={`${INPUT} font-mono`}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium">Distrito</label>
                    <input
                      value={affiliation.district}
                      onChange={(e) => setAffiliation((a) => ({ ...a, district: e.target.value }))}
                      disabled={issue.isPending} className={INPUT}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium">Región</label>
                    <input
                      value={affiliation.region}
                      onChange={(e) => setAffiliation((a) => ({ ...a, region: e.target.value }))}
                      disabled={issue.isPending} className={INPUT}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Payer */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">Recibí de <span className="text-destructive">*</span></label>
                <input
                  value={payerName} onChange={(e) => setPayerName(e.target.value)} disabled={issue.isPending}
                  placeholder="Nombre completo"
                  className={INPUT}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">C.I. <span className="font-normal text-muted-foreground">(opcional)</span></label>
                <input
                  value={payerCi} onChange={(e) => setPayerCi(e.target.value)} disabled={issue.isPending}
                  className={INPUT}
                />
              </div>
            </div>

            {/* Currency */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">Moneda</label>
                <select
                  value={currency} onChange={(e) => setCurrency(e.target.value as ReceiptCurrency)} disabled={issue.isPending}
                  className={INPUT}
                >
                  <option value="BOB">Bolivianos (Bs.)</option>
                  <option value="USD">Dólares (USD)</option>
                </select>
              </div>
              {currency === 'USD' && (
                <div>
                  <label className="mb-1 block text-sm font-medium">T/C <span className="text-destructive">*</span></label>
                  <input
                    type="number" min="0.01" step="0.0001" value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)} disabled={issue.isPending}
                    placeholder="6.9600"
                    className={INPUT}
                  />
                </div>
              )}
            </div>

            {/* Line items */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-sm font-medium">Ítems <span className="text-destructive">*</span></label>
                <button type="button" onClick={addLine} disabled={issue.isPending} className="flex items-center gap-1 text-sm text-primary hover:underline">
                  <Plus className="size-3.5" /> Agregar ítem
                </button>
              </div>
              <div className="space-y-2">
                {lines.map((line, i) => {
                  const lineSubtotal = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
                  return (
                    <div key={i} className="grid grid-cols-6 gap-2 rounded-md border border-border p-2 sm:grid-cols-12">
                      <select
                        value={line.concept_id}
                        onChange={(e) => updateLine(i, { concept_id: e.target.value })}
                        disabled={issue.isPending}
                        aria-label="Concepto"
                        className={`${CELL} col-span-6 sm:col-span-3`}
                      >
                        <option value="">Concepto...</option>
                        {concepts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <input
                        value={line.description}
                        onChange={(e) => updateLine(i, { description: e.target.value })}
                        disabled={issue.isPending}
                        placeholder="Descripción"
                        className={`${CELL} col-span-6 sm:col-span-4`}
                      />
                      <input
                        type="number" min="1" step="1" value={line.quantity}
                        onChange={(e) => updateLine(i, { quantity: e.target.value })}
                        disabled={issue.isPending}
                        aria-label="Cantidad"
                        placeholder="Cant."
                        className={`${CELL} col-span-2 sm:col-span-1`}
                      />
                      <input
                        type="number" min="0" step="0.01" value={line.unit_price}
                        onChange={(e) => updateLine(i, { unit_price: e.target.value })}
                        disabled={issue.isPending}
                        aria-label="Precio unitario"
                        placeholder="P. Unit."
                        className={`${CELL} col-span-2`}
                      />
                      <div className="col-span-1 flex items-center justify-end text-xs font-medium">
                        {lineSubtotal.toFixed(2)}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLine(i)}
                        disabled={issue.isPending || lines.length === 1}
                        aria-label="Quitar ítem"
                        className="col-span-1 flex items-center justify-center text-muted-foreground hover:text-destructive disabled:opacity-30"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Discount + totals */}
            <div className="flex flex-wrap items-start justify-between gap-4 rounded-md bg-muted/30 p-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Descuento</label>
                <input
                  type="number" min="0" step="0.01" value={discount}
                  onChange={(e) => setDiscount(e.target.value)} disabled={issue.isPending}
                  placeholder="0.00"
                  className="w-32 rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div className="text-right text-sm">
                <p className="text-muted-foreground">Subtotal: {subtotal.toFixed(2)} {cur}</p>
                <p className="text-lg font-bold">Total: {total.toFixed(2)} {cur}</p>
                <p className="mt-1 max-w-xs text-xs italic text-muted-foreground">Son: {totalInWords}</p>
              </div>
            </div>

            {/* Payments */}
            {total > 0 && (
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm font-medium">Forma de pago <span className="text-destructive">*</span></label>
                  <button type="button" onClick={addPayment} disabled={issue.isPending} className="flex items-center gap-1 text-sm text-primary hover:underline">
                    <Plus className="size-3.5" /> Dividir pago
                  </button>
                </div>
                <div className="space-y-2">
                  {payments.map((p, i) => (
                    <div key={i} className="grid grid-cols-6 gap-2 rounded-md border border-border p-2 sm:grid-cols-12">
                      <select
                        value={p.method}
                        onChange={(e) => updatePayment(i, { method: e.target.value as ReceiptPaymentMethod })}
                        disabled={issue.isPending}
                        aria-label="Tipo de pago"
                        className={`${CELL} col-span-3`}
                      >
                        {(Object.keys(PAYMENT_METHOD_LABELS) as ReceiptPaymentMethod[])
                          // QR is folded into Transferencia; only kept for a value that already is 'qr'.
                          .filter((m) => m !== 'qr' || p.method === 'qr')
                          .map((m) => (
                            <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>
                          ))}
                      </select>
                      <input
                        type="number" min="0.01" step="0.01" value={p.amount}
                        onChange={(e) => updatePayment(i, { amount: e.target.value })}
                        disabled={issue.isPending}
                        aria-label="Monto del pago"
                        placeholder="Monto"
                        className={`${CELL} col-span-3`}
                      />
                      {p.method === 'check' && (
                        <input
                          value={p.check_number}
                          onChange={(e) => updatePayment(i, { check_number: e.target.value })}
                          disabled={issue.isPending}
                          placeholder="N° cheque"
                          className={`${CELL} col-span-5 sm:col-span-3`}
                        />
                      )}
                      {(p.method === 'bank_transfer' || p.method === 'qr') && bankAccounts.length > 0 && (
                        <select
                          value={p.bank_account_id}
                          onChange={(e) => updatePayment(i, { bank_account_id: e.target.value })}
                          disabled={issue.isPending}
                          aria-label="Cuenta bancaria destino"
                          className={`${CELL} col-span-5 sm:col-span-4`}
                        >
                          <option value="">Cuenta...</option>
                          {bankAccounts.map((b) => <option key={b.id} value={b.id}>{b.account_name} — {b.bank_name}</option>)}
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={() => removePayment(i)}
                        disabled={issue.isPending || payments.length === 1}
                        aria-label="Quitar línea de pago"
                        className="col-span-1 flex items-center justify-center text-muted-foreground hover:text-destructive disabled:opacity-30"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                {Math.abs(paymentsSum - total) > 0.01 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Suma de pagos: {paymentsSum.toFixed(2)} / Total: {total.toFixed(2)}
                  </p>
                )}
              </div>
            )}

            {!isValid && (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <p className="text-xs font-medium text-muted-foreground">Falta completar para emitir:</p>
                <ul className="mt-1 list-inside list-disc text-xs text-muted-foreground">
                  {missing.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={issue.isPending}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => { setError(''); setStep('review'); }}
                disabled={!isValid}
                title={isValid ? undefined : `Falta: ${missing.join(', ')}`}
              >
                Revisar y emitir
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
